import React, { useEffect, useState } from "react";

import api from "../../services/api";
import { downloadBlob } from "../../utils/download";

const formatCurrency = (value, currency = "NGN") =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString(undefined, { hour12: false }) : "-";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }) : "-";

const entryTypeOptions = [
  { value: "", label: "All sources" },
  { value: "general", label: "Manual journal lines" },
  { value: "company", label: "Company postings" },
  { value: "wallet", label: "Wallet approvals" },
  { value: "investment", label: "Investment flows" },
];

const pageSizeOptions = [25, 50, 100, 200];

const getSourceLabel = (entry) => {
  if (entry.source === "general") {
    return "Manual journal";
  }
  if (entry.user?.full_name || entry.user?.username) {
    return entry.user.full_name || entry.user.username;
  }
  if (entry.account?.name) {
    return entry.account.name;
  }
  return entry.account?.code || "?";
};

const LedgerEntries = ({ refreshKey = 0 }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [entryTypeFilter, setEntryTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });

  const buildFilterParams = () => {
    const params = {};
    if (accountFilter) params.account = accountFilter;
    if (directionFilter) params.direction = directionFilter;
    if (entryTypeFilter) params.source = entryTypeFilter;
    return params;
  };

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const params = {
          ...buildFilterParams(),
          page,
          page_size: pageSize,
        };
        const data = await api.adminFetchLedgerEntries(params);
        if (ignore) return;
        setEntries(Array.isArray(data?.items) ? data.items : []);
        const paginationPayload = data?.pagination || {};
        setPagination({
          page: paginationPayload.page || page,
          pages: paginationPayload.pages || 1,
          total: paginationPayload.total ?? (Array.isArray(data?.items) ? data.items.length : 0),
          hasNext: Boolean(paginationPayload.has_next),
          hasPrev: Boolean(paginationPayload.has_prev || (paginationPayload.page || page) > 1),
        });
      } catch (err) {
        if (!ignore) {
          console.error("ledger entries", err);
          setError(err.message || "Unable to load ledger entries");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [accountFilter, directionFilter, entryTypeFilter, page, pageSize, refreshKey]);

  const clearFilters = () => {
    setAccountFilter("");
    setDirectionFilter("");
    setEntryTypeFilter("");
    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleEntryTypeChange = (event) => {
    setEntryTypeFilter(event.target.value);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await api.adminExportLedgerEntries(buildFilterParams());
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 13);
      downloadBlob(blob, `general-ledger-${stamp}.xlsx`);
    } catch (err) {
      alert(err.message || "Unable to export ledger.");
    } finally {
      setExporting(false);
    }
  };

  const currentRangeStart = entries.length ? (pagination.page - 1) * pageSize + 1 : 0;
  const currentRangeEnd = entries.length ? currentRangeStart + entries.length - 1 : 0;

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
      <header className="flex flex-col gap-4 border-b border-slate-100 px-6 py-4">
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">General ledger</h2>
            <p className="text-sm text-slate-500">
              Manual journal lines alongside wallet, company, custodian, and investment postings.
            </p>
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-500">
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-lg border border-emerald-200 px-3 py-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Account code or name"
            value={accountFilter}
            onChange={(event) => {
              setAccountFilter(event.target.value.trim());
              setPage(1);
            }}
            className="w-40 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          />
          <select
            value={directionFilter}
            onChange={(event) => {
              setDirectionFilter(event.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            <option value="">All directions</option>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
          <select
            value={entryTypeFilter}
            onChange={handleEntryTypeChange}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            {entryTypeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Reset
          </button>
        </div>
      </header>

      <div className="px-6 py-4 border-b border-slate-100 text-sm text-slate-600 flex flex-wrap gap-6">
        <span className="text-slate-500">
          Page {pagination.page} of {pagination.pages} - {pagination.total} entries
        </span>
      </div>

      <div className="overflow-x-auto px-6 py-6">
        {loading ? (
          <p className="text-sm text-slate-500">Loading ledger entries...</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : !entries.length ? (
          <p className="text-sm text-slate-500">
            No entries match the current filter. Adjust filters or date window.
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Transaction date</th>
                <th className="px-3 py-2 text-left font-medium">Reference & narration</th>
                <th className="px-3 py-2 text-left font-medium">Account</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-left font-medium">Direction</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Memo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">{formatDate(entry.value_date)}</div>
                    <div className="text-xs text-slate-500">
                      {entry.posted_at ? `Posted ${formatDateTime(entry.posted_at)}` : "Not posted"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">{entry.reference || "-"}</div>
                    <div className="text-xs text-slate-500">{entry.description || "-"}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">{entry.account?.code || "-"}</div>
                    <div className="text-xs text-slate-500">{entry.account?.name || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{getSourceLabel(entry)}</td>
                  <td className="px-3 py-2 capitalize text-slate-600">{entry.direction}</td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatCurrency(entry.amount, entry.currency)}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{entry.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <footer className="flex flex-col gap-3 border-t border-slate-100 px-6 py-4 text-sm text-slate-600 md:flex-row md:items-center md:justify-between">
        <div>
          {entries.length
            ? `Showing ${currentRangeStart}-${currentRangeEnd} of ${pagination.total}`
            : "No entries on this page"}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={!pagination.hasPrev}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!pagination.hasNext}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </footer>
    </section>
  );
};

export default LedgerEntries;
